import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, FileText, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { formatMoney } from '../utils/formatMoney';
import { useToast } from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import FiscalExcelExport from './FiscalExcelExport';
import { LoadFailed } from './settings/instantSettings';
import { cancellationPrintWarning } from '../utils/cancellationPrint';
import {
  DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, ListPage, ListToolbar, Notice, PRIMARY_BTN,
  Stat, Stats, matchesSearch,
} from './shared/listPage';
import {
  FiscalDocument, FiscalDocumentDetails, RETRYABLE_STATUSES, documentTable, fiscalWhen, isCancellable,
  useFiscalDocumentColumns, useFiscalRowLabel, useFiscalTitle,
} from './shared/FiscalDocumentsViewer';

/**
 * Ταμείο → «Παραστατικά» (διαχείριση καταστήματος), στη διάταξη «Πίνακας» (14/10/2026): τα παραστατικά
 * του καταστήματος, η ειδοποίηση για όσα δεν έφτασαν στην ΑΑΔΕ και το συρτάρι με τα στοιχεία, την
 * Επαναποστολή και την Ακύρωση. Ίδια λογική με την παλιά σελίδα (ECRSettings, ως 14/10/2026):
 *  - GET …/fiscal/documents?limit=100[&type] — τα 100 πιο πρόσφατα, φίλτρο τύπου στον server,
 *    αναζήτηση (MARK, UID, πελάτης, ΑΦΜ, σειρά, αριθμός) στη μνήμη, μετρητές από ό,τι φορτώθηκε·
 *  - GET …/fiscal/failed στη φόρτωση και κάθε 30″, «Επαναποστολή όλων» = POST …/fiscal/retry-all·
 *  - POST …/documents/:id/retry και …/cancel (με επιβεβαίωση) όπως πριν·
 *  - «Εξαγωγή σε Excel» (27/10/2026): ΟΛΑ τα παραστατικά μιας περιόδου, όχι μόνο τα 100 — FiscalExcelExport.
 * Στήλες, ετικέτες και στοιχεία του συρταριού είναι ΚΟΙΝΑ με τη λίστα του ταμείου (shared/FiscalDocumentsViewer).
 * Ως 12/10/2026 είχε και καρτέλα «Ρυθμίσεις»: ο πάροχος/token Oxygen και τα TAXISnet είναι πλέον στον
 * super admin (SuperAdminStoresTab), η αρίθμηση σε δική της καταχώριση (settings/DocumentNumberingPage, 13/10).
 */

const LIST_LIMIT = 100;

// Ίδιες επιλογές με πριν (χωρίς 8.6 — η λίστα του ταμείου το έχει).
const TYPE_FILTERS: Array<{ value: string; key: string }> = [
  { value: '11.1', key: 'receipts' },
  { value: '11.2', key: 'serviceReceipts' },
  { value: '1.1', key: 'invoices' },
  { value: '2.1', key: 'serviceInvoices' },
  { value: '11.4', key: 'cancellations' },
  { value: '5.1', key: 'creditNotes' },
];

// Οι τέσσερις μετρητές της σελίδας, πλέον σε μία γραμμή κειμένου (όχι χρωματιστά κουτιά).
const SUMMARY_TYPES = TYPE_FILTERS.slice(0, 4);

type LoadState = 'ready' | 'error' | 'forbidden';

const ECRFiscalDocuments: React.FC = () => {
  const { t } = useTranslation(['settings', 'common', 'orders', 'fiscalDocuments', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const columns = useFiscalDocumentColumns();
  const rowLabel = useFiscalRowLabel();
  const titleOf = useFiscalTitle();
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Fiscal Documents list
  const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([]);
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>('ready');
  const [documentFilter, setDocumentFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<FiscalDocument | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null); // 'retry' | 'cancel' | null

  // Failed documents for alert banner
  const [failedDocuments, setFailedDocuments] = useState<any[]>([]);
  const [retryingAll, setRetryingAll] = useState(false);

  // Fetch failed documents for alert banner
  const fetchFailedDocuments = async () => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/fiscal/failed`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFailedDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching failed documents:', error);
    }
  };

  // Retry all failed documents
  const handleRetryAllFailed = async () => {
    if (!store?.id) return;
    setRetryingAll(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/fiscal/retry-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const result = await response.json();
        showToast(result.success > 0 ? 'success' : 'error', result.message);
        fetchFailedDocuments();
        fetchFiscalDocuments();
      }
    } catch (error) {
      console.error('Error retrying all documents:', error);
      showToast('error', t('settings:ecr.retransmitAllError'));
    } finally {
      setRetryingAll(false);
    }
  };

  // Retry failed document
  const handleRetryDocument = async (documentId: number) => {
    setProcessingAction('retry');
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents/${documentId}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        showToast('success', result.success ? t('settings:ecr.documentTransmitted') : t('settings:ecr.retransmitCheck'));
        setSelectedDocument(null);
        fetchFiscalDocuments();
      } else {
        const error = await response.json();
        showToast('error', error.message || t('settings:ecr.retransmitError'));
      }
    } catch (error) {
      console.error('Error retrying document:', error);
      showToast('error', t('settings:ecr.retransmitError'));
    } finally {
      setProcessingAction(null);
    }
  };

  // Cancel document (issue cancellation receipt)
  // Only for documents with MARK - documents without MARK cannot be cancelled
  const handleCancelDocument = async (documentId: number) => {
    setConfirmModal({
      open: true,
      title: t('settings:ecr.cancelDocument'),
      message: t('settings:ecr.cancelDocumentConfirm'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        setProcessingAction('cancel');
        try {
          const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents/${documentId}/cancel`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
            }
          });

          if (response.ok) {
            const result = await response.json();
            showToast('success', result.success ? t('settings:ecr.documentCancelled') : result.message);
            const printWarning = result.success ? cancellationPrintWarning(result, t) : null;
            if (printWarning) showToast('warning', printWarning);
            setSelectedDocument(null);
            fetchFiscalDocuments();
          } else {
            const error = await response.json();
            showToast('error', error.message || t('settings:ecr.cancelError'));
          }
        } catch (error) {
          console.error('Error cancelling document:', error);
          showToast('error', t('settings:ecr.cancelError'));
        } finally {
          setProcessingAction(null);
        }
      }
    });
  };

  // Fetch fiscal documents
  const fetchFiscalDocuments = async () => {
    setLoadingDocuments(true);
    try {
      let url = `${API_BASE_URL}/api/stores/${store?.id}/fiscal/documents?limit=${LIST_LIMIT}`;
      if (documentFilter !== 'all') {
        url += `&type=${documentFilter}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const list = data.documents || data || [];
        setFiscalDocuments(Array.isArray(list) ? list : []);
        setServerTotal(typeof data.total === 'number' ? data.total : null);
        setLoadState('ready');
      } else {
        // Ως 14/10/2026 μια αποτυχία έδειχνε σιωπηλά «Δεν βρέθηκαν παραστατικά».
        setLoadState(response.status === 403 ? 'forbidden' : 'error');
      }
    } catch (error) {
      console.error('Failed to fetch fiscal documents:', error);
      setLoadState('error');
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (store?.id) {
      fetchFailedDocuments(); // Check for failed documents on load
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  useEffect(() => {
    if (store?.id) {
      fetchFiscalDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id, documentFilter]);

  // Periodically check for failed documents (every 30 seconds)
  useEffect(() => {
    if (!store?.id) return;
    const interval = setInterval(fetchFailedDocuments, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  // Αναζήτηση στη μνήμη — χωρίς τόνους/πεζά (ΚΟΥΛΟΎΡΙ = κουλουρι)· και με το τραπέζι (Π9) από 25/10/2026.
  const filteredDocuments = fiscalDocuments.filter(doc => matchesSearch(
    searchTerm, doc.mark, doc.oxygen_uid, doc.customer_name, doc.customer_vat_number, doc.series, doc.document_number,
    documentTable(doc),
  ));

  // Group documents by type for summary
  const documentsByType = fiscalDocuments.reduce((acc, doc) => {
    const key = doc.invoice_type_code || 'unknown';
    if (!acc[key]) {
      acc[key] = { count: 0, total: 0 };
    }
    acc[key].count++;
    acc[key].total += Number(doc.total_gross_amount) || 0;
    return acc;
  }, {} as Record<string, { count: number; total: number }>);

  // Το παράθυρο επιβεβαίωσης ζει μέσα στο συρτάρι: το Esc κλείνει ΠΡΩΤΑ αυτό.
  const closeDetail = () => {
    if (confirmModal.open) setConfirmModal(prev => ({ ...prev, open: false }));
    else setSelectedDocument(null);
  };

  const firstLoad = loadingDocuments && fiscalDocuments.length === 0;
  const filtered = !!searchTerm.trim() || documentFilter !== 'all';
  const summaryText = () => {
    const count = t('fiscalDocuments:count', { count: filteredDocuments.length });
    // Η λίστα φέρνει τα 100 πιο πρόσφατα· όταν υπάρχουν περισσότερα, το λέει.
    return !searchTerm.trim() && serverTotal !== null && serverTotal > fiscalDocuments.length
      ? `${count} · ${t('fiscalDocuments:latest', { shown: fiscalDocuments.length, total: serverTotal })}`
      : count;
  };

  const typeOptions = TYPE_FILTERS.map(f => ({ value: f.value, label: `${t(`settings:ecr.${f.key}`)} (${f.value})` }));

  const doc = selectedDocument;
  const canRetry = !!doc && RETRYABLE_STATUSES.includes(doc.status);
  const canCancel = !!doc && isCancellable(doc);

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.register')}
      title={t('settings:ecr.title')}
      subtitle={t('settings:ecr.subtitle')}
      testId="fiscal-documents"
    >
      {/* Failed Documents Alert Banner */}
      {failedDocuments.length > 0 && (
        <div className="pb-5" data-testid="fiscal-failed">
          <Notice tone="danger">
            <p className="font-semibold">{t('settings:ecr.aadeConnectionLoss')}</p>
            <p className="mt-0.5">
              {t('settings:ecr.documentsAwaiting', { count: failedDocuments.length })}
              <span className="ml-1.5 opacity-80">{t('settings:ecr.autoRetryHint')}</span>
            </p>
            <div className="mt-3">
              <button type="button" onClick={handleRetryAllFailed} disabled={retryingAll} className={PRIMARY_BTN}>
                {retryingAll
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <RefreshCw className="w-4 h-4" aria-hidden="true" />}
                {retryingAll ? t('settings:ecr.retransmitting') : t('settings:ecr.retransmitAll')}
              </button>
            </div>
          </Notice>
        </div>
      )}

      <div className="pb-4">
        <Stats testId="fiscal-summary">
          {SUMMARY_TYPES.map(({ value, key }) => (
            <Stat
              key={value}
              label={`${t(`settings:ecr.${key}`)} (${value})`}
              value={documentsByType[value]?.count || 0}
              hint={formatMoney(documentsByType[value]?.total || 0)}
            />
          ))}
        </Stats>
      </div>

      <ListToolbar
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: t('settings:ecr.searchPlaceholder'),
          ariaLabel: t('fiscalDocuments:searchLabel'),
        }}
        filters={(
          <FilterSelect
            label={t('fiscalDocuments:typeFilter')}
            value={documentFilter === 'all' ? '' : documentFilter}
            onChange={v => setDocumentFilter(v || 'all')}
            allLabel={t('settings:ecr.allTypes')}
            options={typeOptions}
            testId="fiscal-type-filter"
          />
        )}
        actions={(
          <>
            {store?.id && (
              <FiscalExcelExport
                storeId={store.id}
                typeFilter={documentFilter}
                typeOptions={typeOptions}
                allTypesLabel={t('settings:ecr.allTypes')}
              />
            )}
            <button type="button" className={GHOST_BTN} onClick={fetchFiscalDocuments} disabled={loadingDocuments}>
              <RefreshCw className={`w-4 h-4 ${loadingDocuments ? 'animate-spin' : ''}`} aria-hidden="true" />
              {t('common:refresh')}
            </button>
          </>
        )}
        summary={loadState === 'ready' && !firstLoad ? summaryText() : null}
      />

      {loadState !== 'ready' ? (
        <LoadFailed
          message={loadState === 'forbidden' ? t('fiscalDocuments:forbidden') : t('fiscalDocuments:loadFailed')}
          onRetry={fetchFiscalDocuments}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={filteredDocuments}
          rowKey={d => d.id}
          onRowClick={setSelectedDocument}
          selectedKey={doc?.id ?? null}
          loading={loadingDocuments}
          stickyHeader
          rowLabel={rowLabel}
          empty={(
            <EmptyState
              icon={FileText}
              title={t('settings:ecr.noDocuments')}
              hint={filtered ? t('fiscalDocuments:emptyFilteredHint') : t('fiscalDocuments:emptyHint')}
            />
          )}
          testId="fiscal-table"
        />
      )}

      <DetailPanel
        open={doc !== null}
        onClose={closeDetail}
        title={doc ? titleOf(doc) : ''}
        subtitle={doc ? fiscalWhen(doc.created_at) : undefined}
        footerStart={canCancel && doc ? (
          // Ακύρωση ΜΟΝΟ με MARK (εκδομένο): χωρίς MARK θα άφηνε κενό στην αρίθμηση.
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => handleCancelDocument(doc.id)} disabled={processingAction !== null}>
            {processingAction === 'cancel'
              ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              : <Ban className="w-4 h-4" aria-hidden="true" />}
            {t('settings:ecr.cancelDocument')}
          </button>
        ) : undefined}
        footer={doc ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelectedDocument(null)}>{t('common:close')}</button>
            {canRetry && (
              <button type="button" className={PRIMARY_BTN} onClick={() => handleRetryDocument(doc.id)} disabled={processingAction !== null}>
                {processingAction === 'retry'
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <RotateCcw className="w-4 h-4" aria-hidden="true" />}
                {t('settings:ecr.retransmit')}
              </button>
            )}
          </>
        ) : undefined}
        testId="fiscal-detail"
      >
        {doc && <FiscalDocumentDetails doc={doc} showCode />}
        {/* Μέσα στο συρτάρι: πάνω από αυτό και με το Tab να φτάνει στα κουμπιά του. */}
        <ConfirmModal
          isOpen={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        />
      </DetailPanel>
    </ListPage>
  );
};

export default ECRFiscalDocuments;
